import React from "react";
import {connect} from 'react-redux';
import {Link, Redirect} from "react-router-dom";

import {Button, Form} from "reactstrap";

import ExtMfaShell from "./ExtMfaShell";
import ExtMfaCodeInput from "./ExtMfaCodeInput";
import ExtMfaServerError, {SERVER_ERROR_FALLBACK} from "./ExtMfaServerError";
import ExtLoader from "../../../components/ExtLoader";
import {OutlineAlert} from "../components/ExtAlerts";

import {apiErrorCode, apiErrorMessage, isUnauthorised} from "../../../services/Api";
import {MFA_ERRORS, MFA_CODE_LENGTH} from "../../../config/MfaConfig";
import {
  mfaCleared,
  mfaEnrolled,
  completeMfaLogin,
  fetchUserProfile,
  fetchMfaRegistration,
  confirmMfaEnrolment,
} from "../../../redux/actions/mfaActions";

// First sign-in for an unenrolled factorer / web-interface account:
// GET the QR + secret, then POST the first valid code to finish enrolment.
//
// Named-exported undecorated for unit tests; the default export below is the
// connected version used by the route.
export class ExtMfaEnrol extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      isSubmitting: false,
      isCopying: false,
      qrUri: '',
      secret: '',
      code: '',
      error: '',
      secretError: '',
      isEnrolComplete: false,
      copied: false
    };

    this.isMounted_ = false;
  }

  componentDidMount() {
    this.isMounted_ = true;

    const pending = this.props.mfa.pending;

    if (!pending) {
      // No pending MFA session - either never started, or already completed /
      // abandoned. There is no resume without re-entering the password.
      this.props.history.replace('/extranet/auth/signin');
      return;
    }

    if (pending.mfa_enrolled) {
      // Already enrolled; enrolment was reached by a stale bookmark or cookie.
      this.props.history.replace('/extranet/auth/mfa/verify');
      return;
    }

    this.loadRegistration();
  }

  loadRegistration = () => {
    fetchMfaRegistration()
      .then((data) => {
        if (!this.isMounted_) return;
        this.setState({
          isLoading: false,
          qrUri: data.qr_uri,
          secret: data.secret
        });
      })
      .catch((error) => {
        if (isUnauthorised(error)) {
          this.abandon();
          return;
        }
        if (apiErrorCode(error) === MFA_ERRORS.ALREADY_ENROLLED) {
          // 1023 on the way in: session authenticated + enrolled.
          this.handleAlreadyEnrolled();
          return;
        }
        if (apiErrorCode(error) === MFA_ERRORS.LOCKED_OUT) {
          // 1027 - locked out; show it but keep the retry button so the user
          // can try again once the window lapses.
          if (!this.isMounted_) return;
          this.setState({
            isLoading: false,
            error: apiErrorMessage(error, 'Too many incorrect attempts. Please try again in about 15 minutes.')
          });
          return;
        }
        if (apiErrorCode(error) === MFA_ERRORS.SECRET_UNREADABLE) {
          // 1028 - server-side fault, not retryable. Replaces the form; no
          // "Try again", since retrying cannot succeed.
          if (!this.isMounted_) return;
          this.setState({
            isLoading: false,
            serverError: apiErrorMessage(error, SERVER_ERROR_FALLBACK)
          });
          return;
        }
        if (!this.isMounted_) return;
        this.setState({
          isLoading: false,
          error: apiErrorMessage(error, 'We could not set up two-factor authentication. Please try again.')
        });
      });
  }

  retryLoadRegistration = () => {
    this.setState({isLoading: true, error: ''});
    this.loadRegistration();
  }

  abandon = () => {
    this.props.clearMfa();
    this.props.history.replace('/extranet/auth/signin');
  }

  handleAlreadyEnrolled = () => {
    // 1023 means enrolled - route on what this client already holds
    // (contract section 4). A populated store means a completed login (e.g.
    // double-submit): go straight to the dashboard, no round trip needed.
    if (this.props.user.currentUser.id) {
      this.props.history.replace('/extranet/dashboard');
      return;
    }
    fetchUserProfile()
      .then((data) => {
        // Authenticated session: hydrate and go to the dashboard.
        this.props.completeMfaLogin(data);
        this.props.history.replace('/extranet/dashboard');
      })
      .catch(() => {
        // Pending-only session: correct the stale marker and continue at the
        // verify screen instead of abandoning a still-valid pending session.
        this.props.markEnrolled();
        this.props.history.replace('/extranet/auth/mfa/verify');
      });
  }

  handleChange = (code) => {
    if (!this.state.error) {
      this.setState({code: code});
    } else {
      this.setState({code: code, error: ''});
    }
  }

  canSubmit = () => {
    return (
      !this.state.isLoading &&
      !this.state.isSubmitting &&
      this.state.code.length === MFA_CODE_LENGTH
    );
  }

  handleSubmit = (event) => {
    if (event) event.preventDefault();

    // Debounce: the server locks out repeated failures (contract section 7),
    // so guard against double submits client-side too.
    if (!this.canSubmit()) return;

    this.setState({isSubmitting: true, error: ''});

    confirmMfaEnrolment(this.state.code)
      .then((data) => {
        if (!this.isMounted_) return;
        // Set the confirmation state BEFORE completing the login. The dispatch
        // below re-renders synchronously (React 16 does not batch inside a
        // promise callback), and until isEnrolComplete is set the render guard
        // turns that into a dashboard Redirect - which unmounts this screen
        // before the state lands.
        this.setState({isSubmitting: false, isEnrolComplete: true});
        this.props.completeMfaLogin(data);
      })
      .catch((error) => {
        if (isUnauthorised(error)) {
          this.abandon();
          return;
        }
        if (apiErrorCode(error) === MFA_ERRORS.ALREADY_ENROLLED) {
          // 1023 on submit: enrolled after this screen loaded, session
          // authenticated.
          this.handleAlreadyEnrolled();
          return;
        }

        if (apiErrorCode(error) === MFA_ERRORS.LOCKED_OUT) {
          // 1027 - too many failed attempts, locked out for a while. Stay and
          // show it; the form stays usable (attempts during lockout don't
          // extend it). The QR and secret are unchanged.
          if (!this.isMounted_) return;
          this.setState({
            isSubmitting: false,
            error: apiErrorMessage(error, 'Too many incorrect attempts. Please try again in about 15 minutes.')
          });
          return;
        }

        if (apiErrorCode(error) === MFA_ERRORS.SECRET_UNREADABLE) {
          // 1028 - server-side fault, not retryable: replace the form instead
          // of inviting another submission.
          if (!this.isMounted_) return;
          this.setState({
            isSubmitting: false,
            serverError: apiErrorMessage(error, SERVER_ERROR_FALLBACK)
          });
          return;
        }

        if (!this.isMounted_) return;
        this.setState({
          isSubmitting: false,
          // 1022 - the QR and secret are stable for the account, so keep
          // showing them rather than re-fetching (contract section 4).
          error: apiErrorMessage(error, 'That code is not valid. Please check it and try again.')
        });
      });
  }

  copySecret = () => {
    const secret = this.state.secret;

    const copied = () => this.isMounted_ && this.setState({copied: true});
    const failed = () => this.isMounted_ && this.setState({copied: false});

    // Clipboard API needs a secure context; fall back to a hidden textarea for
    // plain-http dev/staging hosts.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(secret).then(copied, failed);
      return;
    }

    try {
      const field = document.createElement('textarea');
      field.value = secret;
      field.setAttribute('readonly', '');
      field.style.position = 'absolute';
      field.style.left = '-9999px';
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      document.body.removeChild(field);
      copied();
    } catch (e) {
      failed();
    }
  }

  componentWillUnmount() {
    // Async callbacks below may settle after the screen is gone (navigation,
    // refresh); don't setState then.
    this.isMounted_ = false;
  }

  render() {
    const isComplete = this.state.isEnrolComplete;

    // After a successful enrolment the store holds the user, so the
    // confirmation below has to outrank this guard.
    if (this.props.user.currentUser.id && !isComplete) {
      // Already fully signed in (e.g. back button after completing stage 2).
      return <Redirect to='/extranet/dashboard' />;
    }

    return (
      <ExtMfaShell
        title={isComplete ? 'Two-Factor Authentication Enabled' : 'Setup Two-Factor Authentication'}
        lead={isComplete
          ? null
          : 'Use your authenticator app to scan the QR code below. After registering, enter the generated code in the field below and submit to verify.'}
      >
        { isComplete && (
          <React.Fragment>
            <OutlineAlert color="success" dismissable={false}>
              Two-factor authentication (2FA) is now enabled for your CabFare account.
              <br />
              You will need to provide a code from your authentication app for all future logins.
            </OutlineAlert>
            <div className="text-center mt-3">
              <Link to="/extranet/dashboard">
                Click here to continue
              </Link>
            </div>
          </React.Fragment>
        ) }

        { !isComplete && !!this.state.serverError && (
          <ExtMfaServerError message={this.state.serverError} />
        ) }

        { !isComplete && !this.state.serverError && this.state.isLoading && (
          <div>
            <p>Preparing your security key...</p>
            <ExtLoader />
          </div>
        ) }

        { !isComplete && !this.state.serverError && !this.state.isLoading && !!this.state.error && !this.state.qrUri && (
          <React.Fragment>
            <OutlineAlert color="danger">
              {this.state.error}
            </OutlineAlert>
            <div className="text-center">
              <Button color="primary" type="button" onClick={this.retryLoadRegistration}>
                Try again
              </Button>
            </div>
          </React.Fragment>
        ) }

        { !isComplete && !this.state.serverError && !this.state.isLoading && !!this.state.qrUri && (
          <React.Fragment>
            <div className="text-center mb-3">
              <img
                src={this.state.qrUri}
                alt="Two-factor authentication QR code"
                width="200"
                height="200"
              />
            </div>

            <p className="text-center">
              <small>Can&apos;t scan the QR code? Enter this setup key manually:</small>
              <br />
              <code style={{fontSize: '1rem', wordBreak: 'break-all'}}>{this.state.secret}</code>
              <br />
              <Button
                color="link"
                size="sm"
                type="button"
                className="p-0"
                onClick={this.copySecret}
              >
                {this.state.copied ? 'Copied to clipboard' : 'Copy setup key'}
              </Button>
            </p>

            { !!this.state.error && (
              <OutlineAlert color="danger">
                {this.state.error}
              </OutlineAlert>
            ) }

            <Form onSubmit={this.handleSubmit} className="form-login">
              <ExtMfaCodeInput
                id="mfa-enrol-code"
                label="Enter the 6-digit verification code from your authenticator app to complete the process."
                // hint="Codes change every 30 seconds - submit the current one promptly."
                value={this.state.code}
                onChange={this.handleChange}
                onSubmit={this.handleSubmit}
                disabled={this.state.isSubmitting}
              />
              <div className="text-center mt-3">
                <Button
                  color="primary"
                  size="lg"
                  type="submit"
                  disabled={!this.canSubmit()}
                >
                  {this.state.isSubmitting ? 'Verifying...' : 'Verify and sign in'}
                </Button>
              </div>
            </Form>

            <p className="text-center mt-3">
              <small className="text-muted">
                You are not signed in yet. Finish authenticator setup before leaving
                this page or you will need to sign in again.
              </small>
            </p>
          </React.Fragment>
        ) }

        {/* Always available except once enrolled: this is navigation, not a
            retry. Also covers the load-error path, which has no QR block. */}
        { !isComplete && (
        <div className="text-center mt-3">
          <Link to="/extranet/auth/signin" onClick={this.props.clearMfa}>
            Cancel and start over
          </Link>
        </div>
        ) }
      </ExtMfaShell>
    );
  } // render

} // ExtMfaEnrol

const mapStateToProps = store => ({
  mfa: store.mfa,
  user: store.user
})

const mapDispatchToProps = dispatch => ({
  clearMfa: () => dispatch(mfaCleared()),
  markEnrolled: () => dispatch(mfaEnrolled()),
  completeMfaLogin: (payload) => completeMfaLogin(dispatch, payload)
})

export default connect(mapStateToProps, mapDispatchToProps)(ExtMfaEnrol);